// import { useEffect, useRef, useState, useCallback } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { Viewer } from "@photo-sphere-viewer/core";
// import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
// import "@photo-sphere-viewer/core/index.css";
// import "@photo-sphere-viewer/markers-plugin/index.css";
// import { apiJson } from "../api/apiClient";
// import { useSettings } from "../context/SettingsContext";
// import "../styles/ApartmentViewer.css";

// const toRad = (deg) => parseFloat((deg * (Math.PI / 180)).toFixed(6));

// const ApartmentViewer = () => {
//   const { tenant, masterPlanId } = useParams();
//   const containerRef = useRef(null);
//   const viewerRef = useRef(null);
//   const overlayRef = useRef(null);
//   const labelRef = useRef(null);
//   const navigate = useNavigate();

//   const { settings, isLoaded: settingsLoaded } = useSettings();

//   const [loadProgress, setLoadProgress] = useState(0);
//   const [isLoaded, setIsLoaded] = useState(false);
//   const [showHeader, setShowHeader] = useState(false);
//   const [panoramaUrl, setPanoramaUrl] = useState(null);
//   const [hotspots, setHotspots] = useState(null);
//   const [fetchError, setFetchError] = useState(null);
//   const [isFetching, setIsFetching] = useState(true);
//   const [isTransitioning, setIsTransitioning] = useState(false);

//   // ── Summary panel state ─────────────────────────────────────────────────
//   const [blockSummary, setBlockSummary] = useState(null);
//   const [summaryVisible, setSummaryVisible] = useState(true);
//   const [summaryOpen, setSummaryOpen] = useState(true);

//   // ── Helper: build image URL with tenant ──────────────────────────────────
//   const getFullImageUrl = useCallback((path) => {
//     if (!path) return null;
//     // If it's already a full URL, return it as-is
//     if (path.startsWith('http://') || path.startsWith('https://')) {
//       return path;
//     }
//     // Otherwise, build tenant-scoped URL
//     // Use settings-based img_url or fallback to environment variable
//     const baseUrl = settings?.img_url || process.env.REACT_APP_IMG_URL || '';
//     return `${baseUrl}/storage/${path}`;
//   }, [settings]);

//   // ── FETCH 1: hotspots + panorama (critical — viewer depends on this) ────
//   useEffect(() => {
//     if (!tenant || !masterPlanId) {
//       setFetchError("No tenant or master plan ID provided");
//       setIsFetching(false);
//       return;
//     }

//     const fetchCriticalData = async () => {
//       try {
//         // Hotspots - using apiJson for tenant-aware requests
//         const hotspotData = await apiJson(tenant, `/hotspots?master_plan_id=${masterPlanId}`);
//         setHotspots(hotspotData);

//         // Master plan — panorama URL
//         const planData = await apiJson(tenant, `/master-plan/${masterPlanId}`);

//         if (!planData.panorama_url) {
//           throw new Error("No panorama uploaded for this master plan");
//         }

//         // Get the full image URL with tenant context
//         const fullPanoramaUrl = getFullImageUrl(planData.panorama_url);
//         setPanoramaUrl(fullPanoramaUrl);

//         // Kick off the non-critical summary fetch in background
//         if (planData.project_id) {
//           fetchSummaryInBackground(planData.project_id);
//         }
//       } catch (err) {
//         console.error("Critical fetch error:", err);
//         setFetchError(err.message);
//       } finally {
//         setIsFetching(false);
//       }
//     };

//     fetchCriticalData();
//   }, [tenant, masterPlanId, getFullImageUrl]);

//   // ── FETCH 2: block summary (non-critical — runs in background) ──────────
//   const fetchSummaryInBackground = async (projectId) => {
//     try {
//       const data = await apiJson(tenant, `/blocks/summary/${projectId}/${masterPlanId}`);
//       setBlockSummary(data);
//     } catch (err) {
//       // Completely non-fatal — viewer is already running
//       console.warn("Block summary unavailable:", err.message);
//     }
//   };

//   // ── Initialize PSV viewer ───────────────────────────────────────────────
//   useEffect(() => {
//     if (!panoramaUrl || !hotspots || !containerRef.current || viewerRef.current)
//       return;

//     let intervalId;
//     let animationId;
//     let cleanupFunctions = [];

//     const initViewer = async () => {
//       let progress = 0;
//       intervalId = setInterval(() => {
//         progress += Math.random() * 15;
//         if (progress >= 90) {
//           progress = 90;
//           clearInterval(intervalId);
//         }
//         setLoadProgress(Math.min(Math.round(progress), 90));
//       }, 150);

//       const psvMarkers = hotspots.map((h) => ({
//         id: `floor-${h.id}`,
//         position: {
//           yaw: toRad(parseFloat(h.yaw)),
//           pitch: toRad(parseFloat(h.pitch)),
//         },
//         html: `<div class="viewer-marker">${h.label}</div>`,
//         size: { width: 130, height: 42 },
//         tooltip: `Enter ${h.label}`,
//         data: {
//           hotspotId: h.id,
//           blockId: h.block_id,
//           label: h.label,
//           yawRad: toRad(parseFloat(h.yaw)),
//           pitchRad: toRad(parseFloat(h.pitch)),
//         },
//       }));

//       viewerRef.current = new Viewer({
//         container: containerRef.current,
//         panorama: panoramaUrl,
//         defaultZoomLvl: 50,
//         mousewheelCtrlKey: false,
//         loadingImg: null,
//         loadingTxt: "",
//         navbar: ["zoom", "move", "caption", "fullscreen"],
//         plugins: [[MarkersPlugin, { markers: psvMarkers }]],
//       });

//       viewerRef.current.addEventListener("ready", () => {
//         clearInterval(intervalId);
//         setLoadProgress(100);
//         setTimeout(() => {
//           setIsLoaded(true);
//           setShowHeader(true);
//         }, 500);

//         let angle = 0;
//         let isInteracting = false;
//         let resumeTimeout = null;

//         const rotate = () => {
//           if (!isInteracting && viewerRef.current) {
//             angle += 0.003;
//             viewerRef.current.rotate({ yaw: Math.sin(angle) * 0.08, pitch: 0 });
//           }
//           animationId = requestAnimationFrame(rotate);
//         };
//         animationId = requestAnimationFrame(rotate);

//         const onInteractionStart = () => {
//           isInteracting = true;
//           clearTimeout(resumeTimeout);
//         };
//         const onInteractionEnd = () => {
//           clearTimeout(resumeTimeout);
//           resumeTimeout = setTimeout(() => {
//             isInteracting = false;
//           }, 2000);
//         };

//         const container = containerRef.current;
//         container.addEventListener("mousedown", onInteractionStart);
//         container.addEventListener("mouseup", onInteractionEnd);
//         container.addEventListener("touchstart", onInteractionStart);
//         container.addEventListener("touchend", onInteractionEnd);

//         cleanupFunctions.push(() => {
//           cancelAnimationFrame(animationId);
//           clearTimeout(resumeTimeout);
//           container.removeEventListener("mousedown", onInteractionStart);
//           container.removeEventListener("mouseup", onInteractionEnd);
//           container.removeEventListener("touchstart", onInteractionStart);
//           container.removeEventListener("touchend", onInteractionEnd);
//         });
//       });

//       const markersPlugin = viewerRef.current.getPlugin(MarkersPlugin);

//       markersPlugin.addEventListener("select-marker", ({ marker }) => {
//         const { blockId, label, yawRad, pitchRad } = marker.data;

//         hotspots.forEach((h) => {
//           try {
//             markersPlugin.updateMarker({ id: `floor-${h.id}`, opacity: 0 });
//           } catch (e) {}
//         });

//         if (labelRef.current) {
//           labelRef.current.textContent = label;
//           labelRef.current.classList.add("viewer-floor-label-visible");
//         }

//         viewerRef.current
//           .animate({ yaw: yawRad, pitch: pitchRad, zoom: 55, speed: "4rpm" })
//           .then(() =>
//             viewerRef.current.animate({
//               yaw: yawRad,
//               pitch: pitchRad,
//               zoom: 100,
//               speed: "20rpm",
//             }),
//           )
//           .then(() => {
//             setIsTransitioning(true);
//             if (overlayRef.current)
//               overlayRef.current.classList.add("viewer-transition-active");
//             setTimeout(() => navigate(`/${tenant}/floor/${blockId}`), 450);
//           });
//       });

//       cleanupFunctions.push(() => {
//         if (viewerRef.current) {
//           viewerRef.current.destroy();
//           viewerRef.current = null;
//         }
//       });
//     };

//     initViewer();

//     return () => {
//       clearInterval(intervalId);
//       if (animationId) cancelAnimationFrame(animationId);
//       cleanupFunctions.forEach((fn) => fn());
//     };
//   }, [panoramaUrl, hotspots, navigate, tenant]);

//   useEffect(() => () => setIsTransitioning(false), []);

//   const toggleFullscreen = useCallback(() => {
//     if (viewerRef.current) viewerRef.current.toggleFullscreen();
//   }, []);

//   const resetView = useCallback(() => {
//     if (viewerRef.current)
//       viewerRef.current.animate({ yaw: 0, pitch: 0, zoom: 50, speed: "8rpm" });
//   }, []);
  
//   // ── Phase Summary Panel ─────────────────────────────────────────────────
//   const PhaseSummaryPanel = () => {
//     if (!blockSummary || !isLoaded) return null;
//     const { blocks, totals } = blockSummary;

//     // ── No data guard ──────────────────────────────────────────────────────
//     if (!totals || totals.total_plots === 0) {
//       return (
//         <div className="phase-summary-panel">
//           <div className="phase-summary-header">
//             <span className="phase-summary-title">Phase Summary</span>
//           </div>
//           <div className="phase-summary-no-data">
//             <span className="phase-summary-no-data-icon">🏗️</span>
//             <p className="phase-summary-no-data-text">No plot data available</p>
//           </div>
//         </div>
//       );
//     }

//     return (
//       <div className={`phase-summary-panel ${showHeader ? 'phase-summary-panel-with-header' : ''}`}>
//         {/* Toggle header */}
//         <div onClick={() => setSummaryVisible((v) => !v)} className="phase-summary-toggle-header">
//           <span className="phase-summary-title">Phase Summary</span>
//           <span className="phase-summary-toggle-icon">{summaryVisible ? "▲" : "▼"}</span>
//         </div>

//         {summaryVisible && (
//           <>
//             {/* Metric tiles */}
//             <div className="phase-summary-metrics">
//               {[
//                 { label: "Total", value: totals.total_plots, color: "#000000" },
//                 {
//                   label: "Available",
//                   value: totals.available_plot_count,
//                   color: "#4ade80",
//                 },
//                 {
//                   label: "Sold",
//                   value: totals.sold_plot_count,
//                   color: "#f87171",
//                 },
//               ].map(({ label, value, color }) => (
//                 <div key={label} className="phase-summary-metric">
//                   <p className="phase-summary-metric-label">{label}</p>
//                   <p className="phase-summary-metric-value" style={{ color }}>
//                     {value}
//                   </p>
//                 </div>
//               ))}
//             </div>

//             {/* Collapsible list toggle */}
//             <div onClick={() => setSummaryOpen((o) => !o)} className="phase-summary-list-toggle">
//               <span className="phase-summary-list-toggle-label">Phase-wise breakdown</span>
//               <span className="phase-summary-list-toggle-icon">{summaryOpen ? "▴" : "▾"}</span>
//             </div>

//             {/* Block rows */}
//             {summaryOpen && (
//               <ul className="phase-summary-block-list">
//                 {blocks.map((block, i) => {
//                   const isAvail = block.total_plots > 0 && block.available_plot_count > 0;
//                   const hasNoData = block.total_plots === 0;

//                   return (
//                     <li
//                       key={block.id}
//                       onClick={() => isAvail && navigate(`/${tenant}/floor/${block.id}`)}
//                       className={`phase-summary-block-item ${isAvail ? 'phase-summary-block-item-clickable' : ''}`}
//                     >
//                       {/* Status dot */}
//                       <span
//                         className={`phase-summary-status-dot ${
//                           hasNoData ? 'phase-summary-status-dot-no-data' : 
//                           isAvail ? 'phase-summary-status-dot-available' : 'phase-summary-status-dot-sold'
//                         }`}
//                       />

//                       {/* Block name */}
//                       <span className={`phase-summary-block-name ${
//                         hasNoData ? 'phase-summary-block-name-no-data' :
//                         isAvail ? 'phase-summary-block-name-available' : 'phase-summary-block-name-sold'
//                       }`}>
//                         {block.block_name}
//                       </span>

//                       {!hasNoData && (
//                         <>
//                           <span className={`phase-summary-block-available ${
//                             isAvail ? 'phase-summary-block-available-active' : ''
//                           }`}>
//                             {block.available_plot_count}
//                           </span>
//                           <span className="phase-summary-block-total">
//                             /{block.total_plots}
//                           </span>
//                         </>
//                       )}

//                       {/* Sold-out badge */}
//                       {hasNoData && (
//                         <span className="phase-summary-badge phase-summary-badge-no-data">
//                           No data
//                         </span>
//                       )}

//                       {!hasNoData && !isAvail && (
//                         <span className="phase-summary-badge phase-summary-badge-sold">
//                           Sold out
//                         </span>
//                       )}
//                     </li>
//                   );
//                 })}
//               </ul>
//             )}

//             {/* Legend */}
//             <div className="phase-summary-legend">
//               {[
//                 ["#4ade80", "Available"],
//                 ["#6b7280", "Sold out"],
//               ].map(([color, label]) => (
//                 <div key={label} className="phase-summary-legend-item">
//                   <span className="phase-summary-legend-dot" style={{ background: color }} />
//                   <span className="phase-summary-legend-label">{label}</span>
//                 </div>
//               ))}
//             </div>
//           </>
//         )}
//       </div>
//     );
//   };

//   // ── Fetching state ──────────────────────────────────────────────────────
//   if (isFetching || !settingsLoaded) {
//     return (
//       <div className="viewer-fetching">
//         <div className="viewer-fetching-card">
//           <div className="viewer-fetching-icon">🏢</div>
//           <div className="viewer-fetching-text">
//             {!settingsLoaded ? "Loading settings..." : "Loading tour data..."}
//           </div>
//           <div className="viewer-fetching-bar">
//             <div className="viewer-fetching-bar-fill"></div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // ── Error state ─────────────────────────────────────────────────────────
//   if (fetchError || !panoramaUrl) {
//     return (
//       <div className="viewer-error">
//         <div className="viewer-error-card">
//           <div className="viewer-error-icon">⚠️</div>
//           <h2 className="viewer-error-title">Tour Unavailable</h2>
//           <p className="viewer-error-message">
//             {fetchError || "Panorama not uploaded yet for this master plan."}
//           </p>
//           <button onClick={() => navigate(`/${tenant}`)} className="viewer-error-btn">
//             ← Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // ── Main render ─────────────────────────────────────────────────────────
//   return (
//     <div className="apartment-viewer">
//       {/* Header */}
//       <div
//         className={`viewer-header ${showHeader ? "viewer-header-visible" : "viewer-header-hidden"}`}
//       >
//         <div className="viewer-title">
//           <span className="viewer-title-icon">🏢</span>
//           <span>Virtual Tour — Click a floor to explore</span>
//         </div>
//         <div className="viewer-controls">
//           <button onClick={resetView} className="viewer-control-btn">
//             🔄 Reset View
//           </button>
//           <button onClick={toggleFullscreen} className="viewer-control-btn">
//             ⛶ Fullscreen
//           </button>
//           <button onClick={() => navigate(`/${tenant}`)} className="viewer-control-btn">
//             ← Exit
//           </button>
//         </div>
//       </div>

//       {/* 360° panorama canvas */}
//       <div ref={containerRef} className="viewer-panorama" />

//       {/* Phase Summary Panel — floats over panorama */}
//       <PhaseSummaryPanel />

//       {/* Loading overlay */}
//       <div
//         className={`viewer-loading ${isLoaded ? "viewer-loading-hidden" : ""}`}
//       >
//         <div className="viewer-loading-card">
//           <div className="viewer-loading-icon">🏢</div>
//           <div className="viewer-loading-percent">{loadProgress}%</div>
//           <div className="viewer-loading-bar">
//             <div
//               className="viewer-loading-bar-fill"
//               style={{ width: `${loadProgress}%` }}
//             />
//           </div>
//           <div className="viewer-loading-text">
//             {loadProgress < 30 && "Initializing..."}
//             {loadProgress >= 30 &&
//               loadProgress < 60 &&
//               "Downloading panorama..."}
//             {loadProgress >= 60 && loadProgress < 90 && "Processing image..."}
//             {loadProgress >= 90 && loadProgress < 100 && "Almost ready..."}
//             {loadProgress === 100 && "✓ Ready!"}
//           </div>
//         </div>
//       </div>

//       {/* Floor label */}
//       <div ref={labelRef} className="viewer-floor-label" />

//       {/* Transition overlay */}
//       <div ref={overlayRef} className="viewer-transition" />
//     </div>
//   );
// };

// export default ApartmentViewer;


// import { useEffect, useRef, useState, useCallback } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { Viewer } from "@photo-sphere-viewer/core";
// import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
// import "@photo-sphere-viewer/core/index.css";
// import "@photo-sphere-viewer/markers-plugin/index.css";
// import { apiJson } from "../api/apiClient";
// import { useSettings } from "../context/SettingsContext";
// import "../styles/ApartmentViewer.css";

// const toRad = (deg) => parseFloat((deg * (Math.PI / 180)).toFixed(6));

// const ApartmentViewer = () => {
//   const { tenant, masterPlanId } = useParams();
//   const containerRef = useRef(null);
//   const viewerRef = useRef(null);
//   const overlayRef = useRef(null);
//   const labelRef = useRef(null);
//   const navigate = useNavigate();

//   const { settings, isLoaded: settingsLoaded } = useSettings();

//   const [loadProgress, setLoadProgress] = useState(0);
//   const [isLoaded, setIsLoaded] = useState(false);
//   const [showHeader, setShowHeader] = useState(false);
//   const [panoramaUrl, setPanoramaUrl] = useState(null);
//   const [hotspots, setHotspots] = useState(null);
//   const [fetchError, setFetchError] = useState(null);
//   const [isFetching, setIsFetching] = useState(true);
//   const [isTransitioning, setIsTransitioning] = useState(false);
//   const [viewerInitialized, setViewerInitialized] = useState(false);

//   // ── Summary panel state ─────────────────────────────────────────────────
//   const [blockSummary, setBlockSummary] = useState(null);
//   const [summaryVisible, setSummaryVisible] = useState(true);
//   const [summaryOpen, setSummaryOpen] = useState(true);

//   // ── Helper: build image URL with tenant ──────────────────────────────────
//   const getFullImageUrl = useCallback((path) => {
//     if (!path) return null;
    
//     // If it's already a full URL, return it as-is
//     if (path.startsWith('http://') || path.startsWith('https://')) {
//       return path;
//     }
    
//     // Clean the path - remove leading slash if present
//     const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    
//     // Use settings-based img_url or fallback to environment variable
//     const baseUrl = settings?.img_url || process.env.REACT_APP_IMG_URL || '';
    
//     // If baseUrl ends with slash, don't add another one
//     const url = baseUrl.endsWith('/') 
//       ? `${baseUrl}${cleanPath}` 
//       : `${baseUrl}/${cleanPath}`;
    
//     console.log('Generated full image URL:', url);
//     return url;
//   }, [settings]);

//   // ── FETCH 1: hotspots + panorama (critical — viewer depends on this) ────
//   useEffect(() => {
//     if (!tenant || !masterPlanId) {
//       setFetchError("No tenant or master plan ID provided");
//       setIsFetching(false);
//       return;
//     }

//     const fetchCriticalData = async () => {
//       try {
//         console.log('Fetching critical data for tenant:', tenant, 'masterPlanId:', masterPlanId);
        
//         // Hotspots - using apiJson for tenant-aware requests
//         const hotspotData = await apiJson(tenant, `/hotspots?master_plan_id=${masterPlanId}`);
//         console.log('Hotspots fetched:', hotspotData?.length || 0);
//         setHotspots(hotspotData);

//         // Master plan — panorama URL
//         const planData = await apiJson(tenant, `/master-plan/${masterPlanId}`);
//         console.log('Plan data fetched:', planData);

//         if (!planData.panorama_url) {
//           throw new Error("No panorama uploaded for this master plan");
//         }

//         // Get the full image URL with tenant context
//         const fullPanoramaUrl = getFullImageUrl(planData.panorama_url);
//         console.log('Full panorama URL:', fullPanoramaUrl);
        
//         if (!fullPanoramaUrl) {
//           throw new Error("Failed to construct panorama URL");
//         }
        
//         setPanoramaUrl(fullPanoramaUrl);

//         // Kick off the non-critical summary fetch in background
//         if (planData.project_id) {
//           fetchSummaryInBackground(planData.project_id);
//         }
//       } catch (err) {
//         console.error("Critical fetch error:", err);
//         setFetchError(err.message);
//       } finally {
//         setIsFetching(false);
//       }
//     };

//     fetchCriticalData();
//   }, [tenant, masterPlanId, getFullImageUrl]);

//   // ── FETCH 2: block summary (non-critical — runs in background) ──────────
//   const fetchSummaryInBackground = async (projectId) => {
//     try {
//       const data = await apiJson(tenant, `/blocks/summary/${projectId}/${masterPlanId}`);
//       setBlockSummary(data);
//     } catch (err) {
//       // Completely non-fatal — viewer is already running
//       console.warn("Block summary unavailable:", err.message);
//     }
//   };

//   // ── Initialize PSV viewer ───────────────────────────────────────────────
//   useEffect(() => {
//     // Don't proceed if any required data is missing or viewer already exists
//     if (!panoramaUrl || !hotspots || !containerRef.current || viewerRef.current) {
//       console.log('Viewer init conditions not met:', {
//         hasPanorama: !!panoramaUrl,
//         hasHotspots: !!hotspots,
//         hasContainer: !!containerRef.current,
//         hasViewer: !!viewerRef.current
//       });
//       return;
//     }

//     console.log('Initializing viewer with panorama:', panoramaUrl);

//     let intervalId;
//     let animationId;
//     let cleanupFunctions = [];
//     let viewerInstance = null;

//     const initViewer = async () => {
//       try {
//         // Reset progress
//         let progress = 0;
//         intervalId = setInterval(() => {
//           progress += Math.random() * 15;
//           if (progress >= 90) {
//             progress = 90;
//             clearInterval(intervalId);
//           }
//           setLoadProgress(Math.min(Math.round(progress), 90));
//         }, 150);

//         const psvMarkers = hotspots.map((h) => ({
//           id: `floor-${h.id}`,
//           position: {
//             yaw: toRad(parseFloat(h.yaw)),
//             pitch: toRad(parseFloat(h.pitch)),
//           },
//           html: `<div class="viewer-marker">${h.label}</div>`,
//           size: { width: 130, height: 42 },
//           tooltip: `Enter ${h.label}`,
//           data: {
//             hotspotId: h.id,
//             blockId: h.block_id,
//             label: h.label,
//             yawRad: toRad(parseFloat(h.yaw)),
//             pitchRad: toRad(parseFloat(h.pitch)),
//           },
//         }));

//         // Create viewer instance
//         viewerInstance = new Viewer({
//           container: containerRef.current,
//           panorama: panoramaUrl,
//           defaultZoomLvl: 50,
//           mousewheelCtrlKey: false,
//           loadingImg: null,
//           loadingTxt: "",
//           navbar: ["zoom", "move", "caption", "fullscreen"],
//           plugins: [[MarkersPlugin, { markers: psvMarkers }]],
//         });

//         viewerRef.current = viewerInstance;

//         // Handle ready event
//         viewerInstance.addEventListener("ready", () => {
//           console.log('Viewer ready');
//           clearInterval(intervalId);
//           setLoadProgress(100);
          
//           setTimeout(() => {
//             setIsLoaded(true);
//             setShowHeader(true);
//             setViewerInitialized(true);
//           }, 500);

//           // Auto-rotate effect
//           let angle = 0;
//           let isInteracting = false;
//           let resumeTimeout = null;

//           const rotate = () => {
//             if (!isInteracting && viewerRef.current) {
//               angle += 0.003;
//               try {
//                 viewerRef.current.rotate({ yaw: Math.sin(angle) * 0.08, pitch: 0 });
//               } catch (e) {
//                 // Ignore rotation errors
//               }
//             }
//             if (viewerRef.current) {
//               animationId = requestAnimationFrame(rotate);
//             }
//           };
//           animationId = requestAnimationFrame(rotate);

//           const onInteractionStart = () => {
//             isInteracting = true;
//             clearTimeout(resumeTimeout);
//           };

//           const onInteractionEnd = () => {
//             clearTimeout(resumeTimeout);
//             resumeTimeout = setTimeout(() => {
//               isInteracting = false;
//             }, 2000);
//           };

//           const container = containerRef.current;
//           if (container) {
//             container.addEventListener("mousedown", onInteractionStart);
//             container.addEventListener("mouseup", onInteractionEnd);
//             container.addEventListener("touchstart", onInteractionStart);
//             container.addEventListener("touchend", onInteractionEnd);
//           }

//           cleanupFunctions.push(() => {
//             if (animationId) cancelAnimationFrame(animationId);
//             clearTimeout(resumeTimeout);
//             if (container) {
//               container.removeEventListener("mousedown", onInteractionStart);
//               container.removeEventListener("mouseup", onInteractionEnd);
//               container.removeEventListener("touchstart", onInteractionStart);
//               container.removeEventListener("touchend", onInteractionEnd);
//             }
//           });
//         });

//         // Handle error event
//         viewerInstance.addEventListener("error", (e) => {
//           console.error('Viewer error:', e);
//           setFetchError(`Failed to load panorama: ${e.message || 'Unknown error'}`);
//         });

//         // Handle markers plugin
//         const markersPlugin = viewerInstance.getPlugin(MarkersPlugin);
//         if (markersPlugin) {
//           markersPlugin.addEventListener("select-marker", ({ marker }) => {
//             const { blockId, label, yawRad, pitchRad } = marker.data;

//             hotspots.forEach((h) => {
//               try {
//                 markersPlugin.updateMarker({ id: `floor-${h.id}`, opacity: 0 });
//               } catch (e) {}
//             });

//             if (labelRef.current) {
//               labelRef.current.textContent = label;
//               labelRef.current.classList.add("viewer-floor-label-visible");
//             }

//             viewerInstance
//               ?.animate({ yaw: yawRad, pitch: pitchRad, zoom: 55, speed: "4rpm" })
//               .then(() =>
//                 viewerInstance?.animate({
//                   yaw: yawRad,
//                   pitch: pitchRad,
//                   zoom: 100,
//                   speed: "20rpm",
//                 }),
//               )
//               .then(() => {
//                 setIsTransitioning(true);
//                 if (overlayRef.current)
//                   overlayRef.current.classList.add("viewer-transition-active");
//                 setTimeout(() => navigate(`/${tenant}/floor/${blockId}`), 450);
//               });
//           });
//         }

//         cleanupFunctions.push(() => {
//           if (viewerRef.current) {
//             try {
//               viewerRef.current.destroy();
//             } catch (e) {
//               console.warn('Error destroying viewer:', e);
//             }
//             viewerRef.current = null;
//             setViewerInitialized(false);
//           }
//         });

//       } catch (err) {
//         console.error('Error initializing viewer:', err);
//         setFetchError(`Failed to initialize viewer: ${err.message}`);
//         clearInterval(intervalId);
//       }
//     };

//     initViewer();

//     return () => {
//       clearInterval(intervalId);
//       if (animationId) cancelAnimationFrame(animationId);
//       cleanupFunctions.forEach((fn) => {
//         try {
//           fn();
//         } catch (e) {
//           console.warn('Error during cleanup:', e);
//         }
//       });
//     };
//   }, [panoramaUrl, hotspots, navigate, tenant]);

//   useEffect(() => () => setIsTransitioning(false), []);

//   const toggleFullscreen = useCallback(() => {
//     if (viewerRef.current) {
//       try {
//         viewerRef.current.toggleFullscreen();
//       } catch (e) {
//         console.warn('Fullscreen error:', e);
//       }
//     }
//   }, []);

//   const resetView = useCallback(() => {
//     if (viewerRef.current) {
//       try {
//         viewerRef.current.animate({ yaw: 0, pitch: 0, zoom: 50, speed: "8rpm" });
//       } catch (e) {
//         console.warn('Reset view error:', e);
//       }
//     }
//   }, []);
  
//   // ── Phase Summary Panel ─────────────────────────────────────────────────
//   const PhaseSummaryPanel = () => {
//     if (!blockSummary || !isLoaded) return null;
//     const { blocks, totals } = blockSummary;

//     // ── No data guard ──────────────────────────────────────────────────────
//     if (!totals || totals.total_plots === 0) {
//       return (
//         <div className="phase-summary-panel">
//           <div className="phase-summary-header">
//             <span className="phase-summary-title">Phase Summary</span>
//           </div>
//           <div className="phase-summary-no-data">
//             <span className="phase-summary-no-data-icon">🏗️</span>
//             <p className="phase-summary-no-data-text">No plot data available</p>
//           </div>
//         </div>
//       );
//     }

//     return (
//       <div className={`phase-summary-panel ${showHeader ? 'phase-summary-panel-with-header' : ''}`}>
//         {/* Toggle header */}
//         <div onClick={() => setSummaryVisible((v) => !v)} className="phase-summary-toggle-header">
//           <span className="phase-summary-title">Phase Summary</span>
//           <span className="phase-summary-toggle-icon">{summaryVisible ? "▲" : "▼"}</span>
//         </div>

//         {summaryVisible && (
//           <>
//             {/* Metric tiles */}
//             <div className="phase-summary-metrics">
//               {[
//                 { label: "Total", value: totals.total_plots, color: "#000000" },
//                 {
//                   label: "Available",
//                   value: totals.available_plot_count,
//                   color: "#4ade80",
//                 },
//                 {
//                   label: "Sold",
//                   value: totals.sold_plot_count,
//                   color: "#f87171",
//                 },
//               ].map(({ label, value, color }) => (
//                 <div key={label} className="phase-summary-metric">
//                   <p className="phase-summary-metric-label">{label}</p>
//                   <p className="phase-summary-metric-value" style={{ color }}>
//                     {value}
//                   </p>
//                 </div>
//               ))}
//             </div>

//             {/* Collapsible list toggle */}
//             <div onClick={() => setSummaryOpen((o) => !o)} className="phase-summary-list-toggle">
//               <span className="phase-summary-list-toggle-label">Phase-wise breakdown</span>
//               <span className="phase-summary-list-toggle-icon">{summaryOpen ? "▴" : "▾"}</span>
//             </div>

//             {/* Block rows */}
//             {summaryOpen && (
//               <ul className="phase-summary-block-list">
//                 {blocks.map((block, i) => {
//                   const isAvail = block.total_plots > 0 && block.available_plot_count > 0;
//                   const hasNoData = block.total_plots === 0;

//                   return (
//                     <li
//                       key={block.id}
//                       onClick={() => isAvail && navigate(`/${tenant}/floor/${block.id}`)}
//                       className={`phase-summary-block-item ${isAvail ? 'phase-summary-block-item-clickable' : ''}`}
//                     >
//                       {/* Status dot */}
//                       <span
//                         className={`phase-summary-status-dot ${
//                           hasNoData ? 'phase-summary-status-dot-no-data' : 
//                           isAvail ? 'phase-summary-status-dot-available' : 'phase-summary-status-dot-sold'
//                         }`}
//                       />

//                       {/* Block name */}
//                       <span className={`phase-summary-block-name ${
//                         hasNoData ? 'phase-summary-block-name-no-data' :
//                         isAvail ? 'phase-summary-block-name-available' : 'phase-summary-block-name-sold'
//                       }`}>
//                         {block.block_name}
//                       </span>

//                       {!hasNoData && (
//                         <>
//                           <span className={`phase-summary-block-available ${
//                             isAvail ? 'phase-summary-block-available-active' : ''
//                           }`}>
//                             {block.available_plot_count}
//                           </span>
//                           <span className="phase-summary-block-total">
//                             /{block.total_plots}
//                           </span>
//                         </>
//                       )}

//                       {/* Sold-out badge */}
//                       {hasNoData && (
//                         <span className="phase-summary-badge phase-summary-badge-no-data">
//                           No data
//                         </span>
//                       )}

//                       {!hasNoData && !isAvail && (
//                         <span className="phase-summary-badge phase-summary-badge-sold">
//                           Sold out
//                         </span>
//                       )}
//                     </li>
//                   );
//                 })}
//               </ul>
//             )}

//             {/* Legend */}
//             <div className="phase-summary-legend">
//               {[
//                 ["#4ade80", "Available"],
//                 ["#6b7280", "Sold out"],
//               ].map(([color, label]) => (
//                 <div key={label} className="phase-summary-legend-item">
//                   <span className="phase-summary-legend-dot" style={{ background: color }} />
//                   <span className="phase-summary-legend-label">{label}</span>
//                 </div>
//               ))}
//             </div>
//           </>
//         )}
//       </div>
//     );
//   };

//   // ── Fetching state ──────────────────────────────────────────────────────
//   if (isFetching || !settingsLoaded) {
//     return (
//       <div className="viewer-fetching">
//         <div className="viewer-fetching-card">
//           <div className="viewer-fetching-icon">🏢</div>
//           <div className="viewer-fetching-text">
//             {!settingsLoaded ? "Loading settings..." : "Loading tour data..."}
//           </div>
//           <div className="viewer-fetching-bar">
//             <div className="viewer-fetching-bar-fill"></div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // ── Error state ─────────────────────────────────────────────────────────
//   if (fetchError || !panoramaUrl) {
//     return (
//       <div className="viewer-error">
//         <div className="viewer-error-card">
//           <div className="viewer-error-icon">⚠️</div>
//           <h2 className="viewer-error-title">Tour Unavailable</h2>
//           <p className="viewer-error-message">
//             {fetchError || "Panorama not uploaded yet for this master plan."}
//           </p>
//           <button onClick={() => navigate(`/${tenant}`)} className="viewer-error-btn">
//             ← Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // ── Main render ─────────────────────────────────────────────────────────
//   return (
//     <div className="apartment-viewer">
//       {/* Header */}
//       <div
//         className={`viewer-header ${showHeader ? "viewer-header-visible" : "viewer-header-hidden"}`}
//       >
//         <div className="viewer-title">
//           <span className="viewer-title-icon">🏢</span>
//           <span>Virtual Tour — Click a floor to explore</span>
//         </div>
//         <div className="viewer-controls">
//           <button onClick={resetView} className="viewer-control-btn">
//             🔄 Reset View
//           </button>
//           <button onClick={toggleFullscreen} className="viewer-control-btn">
//             ⛶ Fullscreen
//           </button>
//           <button onClick={() => navigate(`/${tenant}`)} className="viewer-control-btn">
//             ← Exit
//           </button>
//         </div>
//       </div>

//       {/* 360° panorama canvas */}
//       <div ref={containerRef} className="viewer-panorama" />

//       {/* Phase Summary Panel — floats over panorama */}
//       <PhaseSummaryPanel />

//       {/* Loading overlay */}
//       <div
//         className={`viewer-loading ${isLoaded ? "viewer-loading-hidden" : ""}`}
//       >
//         <div className="viewer-loading-card">
//           <div className="viewer-loading-icon">🏢</div>
//           <div className="viewer-loading-percent">{loadProgress}%</div>
//           <div className="viewer-loading-bar">
//             <div
//               className="viewer-loading-bar-fill"
//               style={{ width: `${loadProgress}%` }}
//             />
//           </div>
//           <div className="viewer-loading-text">
//             {loadProgress < 30 && "Initializing..."}
//             {loadProgress >= 30 &&
//               loadProgress < 60 &&
//               "Downloading panorama..."}
//             {loadProgress >= 60 && loadProgress < 90 && "Processing image..."}
//             {loadProgress >= 90 && loadProgress < 100 && "Almost ready..."}
//             {loadProgress === 100 && "✓ Ready!"}
//           </div>
//         </div>
//       </div>

//       {/* Floor label */}
//       <div ref={labelRef} className="viewer-floor-label" />

//       {/* Transition overlay */}
//       <div ref={overlayRef} className="viewer-transition" />
//     </div>
//   );
// };

// export default ApartmentViewer;

// import { useEffect, useRef, useState, useCallback } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { Viewer } from "@photo-sphere-viewer/core";
// import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
// import "@photo-sphere-viewer/core/index.css";
// import "@photo-sphere-viewer/markers-plugin/index.css";
// import { apiJson } from "../api/apiClient";
// import { useSettings } from "../context/SettingsContext";
// import "../styles/ApartmentViewer.css";

// const toRad = (deg) => parseFloat((deg * (Math.PI / 180)).toFixed(6));

// // ── Tenant-scoped auth guard ────────────────────────────────────────────
// // ASSUMPTION: logged-in user info lives in localStorage as `auth_user` /
// // `auth_token`, tagged with the tenant it belongs to under `auth_tenant`.
// // If you're using an AuthContext/Provider instead, move this exact check
// // into that provider's init effect, keyed off the tenant from the URL —
// // the principle (clear identity the instant the URL tenant doesn't match
// // the stored tenant) is what matters, not where it physically lives.
// const useTenantScopedAuth = (tenant) => {
//   useEffect(() => {
//     if (!tenant) return;
//     try {
//       const storedTenant = localStorage.getItem("auth_tenant");
//       if (storedTenant && storedTenant !== tenant) {
//         localStorage.removeItem("auth_user");
//         localStorage.removeItem("auth_token");
//         localStorage.removeItem("auth_tenant");
//       }
//     } catch (e) {
//       console.warn("Tenant auth scope check failed:", e);
//     }
//   }, [tenant]);
// };

// const ApartmentViewer = () => {
//   const { tenant, masterPlanId } = useParams();
//   const containerRef = useRef(null);
//   const viewerRef = useRef(null);
//   const overlayRef = useRef(null);
//   const labelRef = useRef(null);
//   const navigate = useNavigate();

//   useTenantScopedAuth(tenant);

//   const { settings, isLoaded: settingsLoaded } = useSettings();

//   const [loadProgress, setLoadProgress] = useState(0);
//   const [isLoaded, setIsLoaded] = useState(false);
//   const [showHeader, setShowHeader] = useState(false);
//   const [panoramaUrl, setPanoramaUrl] = useState(null);
//   const [hotspots, setHotspots] = useState(null);
//   const [fetchError, setFetchError] = useState(null);
//   const [isFetching, setIsFetching] = useState(true);
//   const [isTransitioning, setIsTransitioning] = useState(false);
//   const [viewerInitialized, setViewerInitialized] = useState(false);

//   // ── Summary panel state ─────────────────────────────────────────────────
//   const [blockSummary, setBlockSummary] = useState(null);
//   const [summaryVisible, setSummaryVisible] = useState(true);
//   const [summaryOpen, setSummaryOpen] = useState(true);

//   // ── Helper: build image URL with tenant ──────────────────────────────────
//   const getFullImageUrl = useCallback((path) => {
//     if (!path) return null;

//     // If it's already a full URL, return it as-is
//     if (path.startsWith('http://') || path.startsWith('https://')) {
//       return path;
//     }

//     // Clean the path - remove leading slash if present
//     const cleanPath = path.startsWith('/') ? path.substring(1) : path;

//     // Use settings-based img_url or fallback to environment variable
//     const baseUrl = settings?.img_url || process.env.REACT_APP_IMG_URL || '';

//     // If baseUrl ends with slash, don't add another one
//     const url = baseUrl.endsWith('/')
//       ? `${baseUrl}${cleanPath}`
//       : `${baseUrl}/${cleanPath}`;

//     return url;
//   }, [settings]);

//   // ── Find a hotspot's block stats from the background-loaded summary ────
//   const getBlockStats = useCallback(
//     (blockId) => {
//       if (!blockSummary?.blocks) return null;
//       const block = blockSummary.blocks.find((b) => b.id === blockId);
//       if (!block) return null;
//       return {
//         available: block.available_plot_count,
//         total: block.total_plots,
//       };
//     },
//     [blockSummary],
//   );

//   // ── Build a marker's inner HTML — dot + hover availability popup ───────
//   const buildMarkerHtml = useCallback((h, stats) => {
//     const body = stats
//       ? `<div class="viewer-marker-popup__stats">
//            <span class="viewer-marker-popup__available">${stats.available}</span>
//            <span class="viewer-marker-popup__sep">/</span>
//            <span class="viewer-marker-popup__total">${stats.total}</span>
//          </div>
//          <div class="viewer-marker-popup__word">
//            ${stats.available > 0 ? "plots available" : "sold out"}
//          </div>`
//       : `<div class="viewer-marker-popup__word">Checking availability…</div>`;

//     return `
//       <div class="viewer-marker-dot">
//         <span class="viewer-marker-dot__core"></span>
//         <span class="viewer-marker-dot__ring"></span>
//         <div class="viewer-marker-popup ${stats && stats.available === 0 ? "is-sold-out" : ""}">
//           <div class="viewer-marker-popup__label">${h.label}</div>
//           ${body}
//         </div>
//       </div>`;
//   }, []);

//   // ── FETCH 1: hotspots + panorama (critical — viewer depends on this) ────
//   useEffect(() => {
//     if (!tenant || !masterPlanId) {
//       setFetchError("No tenant or master plan ID provided");
//       setIsFetching(false);
//       return;
//     }

//     const fetchCriticalData = async () => {
//       try {
//         // Hotspots - using apiJson for tenant-aware requests
//         const hotspotData = await apiJson(tenant, `/hotspots?master_plan_id=${masterPlanId}`);
//         setHotspots(hotspotData);

//         // Master plan — panorama URL
//         const planData = await apiJson(tenant, `/master-plan/${masterPlanId}`);

//         if (!planData.panorama_url) {
//           throw new Error("No panorama uploaded for this master plan");
//         }

//         // Get the full image URL with tenant context
//         const fullPanoramaUrl = getFullImageUrl(planData.panorama_url);

//         if (!fullPanoramaUrl) {
//           throw new Error("Failed to construct panorama URL");
//         }

//         setPanoramaUrl(fullPanoramaUrl);

//         // Kick off the non-critical summary fetch in background
//         if (planData.project_id) {
//           fetchSummaryInBackground(planData.project_id);
//         }
//       } catch (err) {
//         console.error("Critical fetch error:", err);
//         setFetchError(err.message);
//       } finally {
//         setIsFetching(false);
//       }
//     };

//     fetchCriticalData();
//   }, [tenant, masterPlanId, getFullImageUrl]);

//   // ── FETCH 2: block summary (non-critical — runs in background) ──────────
//   const fetchSummaryInBackground = async (projectId) => {
//     try {
//       const data = await apiJson(tenant, `/blocks/summary/${projectId}/${masterPlanId}`);
//       setBlockSummary(data);
//     } catch (err) {
//       // Completely non-fatal — viewer is already running
//       console.warn("Block summary unavailable:", err.message);
//     }
//   };

//   // ── Initialize PSV viewer ───────────────────────────────────────────────
//   useEffect(() => {
//     // Don't proceed if any required data is missing or viewer already exists
//     if (!panoramaUrl || !hotspots || !containerRef.current || viewerRef.current) {
//       return;
//     }

//     let intervalId;
//     let animationId;
//     let cleanupFunctions = [];
//     let viewerInstance = null;

//     const initViewer = async () => {
//       try {
//         // Reset progress
//         let progress = 0;
//         intervalId = setInterval(() => {
//           progress += Math.random() * 15;
//           if (progress >= 90) {
//             progress = 90;
//             clearInterval(intervalId);
//           }
//           setLoadProgress(Math.min(Math.round(progress), 90));
//         }, 150);

//         const psvMarkers = hotspots.map((h) => ({
//           id: `floor-${h.id}`,
//           position: {
//             yaw: toRad(parseFloat(h.yaw)),
//             pitch: toRad(parseFloat(h.pitch)),
//           },
//           html: buildMarkerHtml(h, getBlockStats(h.block_id)),
//           size: { width: 22, height: 22 },
//           anchor: "center center",
//           data: {
//             hotspotId: h.id,
//             blockId: h.block_id,
//             label: h.label,
//             yawRad: toRad(parseFloat(h.yaw)),
//             pitchRad: toRad(parseFloat(h.pitch)),
//           },
//         }));

//         // Create viewer instance
//         viewerInstance = new Viewer({
//           container: containerRef.current,
//           panorama: panoramaUrl,
//           defaultZoomLvl: 50,
//           mousewheelCtrlKey: false,
//           loadingImg: null,
//           loadingTxt: "",
//           navbar: ["zoom", "move", "caption", "fullscreen"],
//           plugins: [[MarkersPlugin, { markers: psvMarkers }]],
//         });

//         viewerRef.current = viewerInstance;

//         // Handle ready event
//         viewerInstance.addEventListener("ready", () => {
//           clearInterval(intervalId);
//           setLoadProgress(100);

//           setTimeout(() => {
//             setIsLoaded(true);
//             setShowHeader(true);
//             setViewerInitialized(true);
//           }, 500);

//           // Auto-rotate effect
//           let angle = 0;
//           let isInteracting = false;
//           let resumeTimeout = null;

//           const rotate = () => {
//             if (!isInteracting && viewerRef.current) {
//               angle += 0.003;
//               try {
//                 viewerRef.current.rotate({ yaw: Math.sin(angle) * 0.08, pitch: 0 });
//               } catch (e) {
//                 // Ignore rotation errors
//               }
//             }
//             if (viewerRef.current) {
//               animationId = requestAnimationFrame(rotate);
//             }
//           };
//           animationId = requestAnimationFrame(rotate);

//           const onInteractionStart = () => {
//             isInteracting = true;
//             clearTimeout(resumeTimeout);
//           };

//           const onInteractionEnd = () => {
//             clearTimeout(resumeTimeout);
//             resumeTimeout = setTimeout(() => {
//               isInteracting = false;
//             }, 2000);
//           };

//           const container = containerRef.current;
//           if (container) {
//             container.addEventListener("mousedown", onInteractionStart);
//             container.addEventListener("mouseup", onInteractionEnd);
//             container.addEventListener("touchstart", onInteractionStart);
//             container.addEventListener("touchend", onInteractionEnd);
//           }

//           cleanupFunctions.push(() => {
//             if (animationId) cancelAnimationFrame(animationId);
//             clearTimeout(resumeTimeout);
//             if (container) {
//               container.removeEventListener("mousedown", onInteractionStart);
//               container.removeEventListener("mouseup", onInteractionEnd);
//               container.removeEventListener("touchstart", onInteractionStart);
//               container.removeEventListener("touchend", onInteractionEnd);
//             }
//           });
//         });

//         // Handle error event
//         viewerInstance.addEventListener("error", (e) => {
//           console.error('Viewer error:', e);
//           setFetchError(`Failed to load panorama: ${e.message || 'Unknown error'}`);
//         });

//         // Handle markers plugin
//         const markersPlugin = viewerInstance.getPlugin(MarkersPlugin);
//         if (markersPlugin) {
//           markersPlugin.addEventListener("select-marker", ({ marker }) => {
//             const { blockId, label, yawRad, pitchRad } = marker.data;

//             hotspots.forEach((h) => {
//               try {
//                 markersPlugin.updateMarker({ id: `floor-${h.id}`, opacity: 0 });
//               } catch (e) {}
//             });

//             if (labelRef.current) {
//               labelRef.current.textContent = label;
//               labelRef.current.classList.add("viewer-floor-label-visible");
//             }

//             viewerInstance
//               ?.animate({ yaw: yawRad, pitch: pitchRad, zoom: 55, speed: "4rpm" })
//               .then(() =>
//                 viewerInstance?.animate({
//                   yaw: yawRad,
//                   pitch: pitchRad,
//                   zoom: 100,
//                   speed: "20rpm",
//                 }),
//               )
//               .then(() => {
//                 setIsTransitioning(true);
//                 if (overlayRef.current)
//                   overlayRef.current.classList.add("viewer-transition-active");
//                 setTimeout(() => navigate(`/${tenant}/floor/${blockId}`), 450);
//               });
//           });
//         }

//         cleanupFunctions.push(() => {
//           if (viewerRef.current) {
//             try {
//               viewerRef.current.destroy();
//             } catch (e) {
//               console.warn('Error destroying viewer:', e);
//             }
//             viewerRef.current = null;
//             setViewerInitialized(false);
//           }
//         });

//       } catch (err) {
//         console.error('Error initializing viewer:', err);
//         setFetchError(`Failed to initialize viewer: ${err.message}`);
//         clearInterval(intervalId);
//       }
//     };

//     initViewer();

//     return () => {
//       clearInterval(intervalId);
//       if (animationId) cancelAnimationFrame(animationId);
//       cleanupFunctions.forEach((fn) => {
//         try {
//           fn();
//         } catch (e) {
//           console.warn('Error during cleanup:', e);
//         }
//       });
//     };
//   }, [panoramaUrl, hotspots, navigate, tenant, buildMarkerHtml, getBlockStats]);

//   // ── Refresh marker popups once block summary arrives in background ─────
//   useEffect(() => {
//     if (!viewerRef.current || !hotspots) return;
//     const markersPlugin = viewerRef.current.getPlugin(MarkersPlugin);
//     if (!markersPlugin) return;

//     hotspots.forEach((h) => {
//       try {
//         markersPlugin.updateMarker({
//           id: `floor-${h.id}`,
//           html: buildMarkerHtml(h, getBlockStats(h.block_id)),
//         });
//       } catch (e) {
//         // Marker may not exist yet if hotspots changed mid-flight — ignore.
//       }
//     });
//   }, [blockSummary, hotspots, buildMarkerHtml, getBlockStats]);

//   useEffect(() => () => setIsTransitioning(false), []);

//   const toggleFullscreen = useCallback(() => {
//     if (viewerRef.current) {
//       try {
//         viewerRef.current.toggleFullscreen();
//       } catch (e) {
//         console.warn('Fullscreen error:', e);
//       }
//     }
//   }, []);

//   const resetView = useCallback(() => {
//     if (viewerRef.current) {
//       try {
//         viewerRef.current.animate({ yaw: 0, pitch: 0, zoom: 50, speed: "8rpm" });
//       } catch (e) {
//         console.warn('Reset view error:', e);
//       }
//     }
//   }, []);

//   // ── Phase Summary Panel ─────────────────────────────────────────────────
//   const PhaseSummaryPanel = () => {
//     if (!blockSummary || !isLoaded) return null;
//     const { blocks, totals } = blockSummary;

//     // ── No data guard ──────────────────────────────────────────────────────
//     if (!totals || totals.total_plots === 0) {
//       return (
//         <div className="phase-summary-panel">
//           <div className="phase-summary-header">
//             <span className="phase-summary-title">Phase Summary</span>
//           </div>
//           <div className="phase-summary-no-data">
//             <span className="phase-summary-no-data-icon">🏗️</span>
//             <p className="phase-summary-no-data-text">No plot data available</p>
//           </div>
//         </div>
//       );
//     }

//     return (
//       <div className={`phase-summary-panel ${showHeader ? 'phase-summary-panel-with-header' : ''}`}>
//         {/* Toggle header */}
//         <div onClick={() => setSummaryVisible((v) => !v)} className="phase-summary-toggle-header">
//           <span className="phase-summary-title">Phase Summary</span>
//           <span className="phase-summary-toggle-icon">{summaryVisible ? "▲" : "▼"}</span>
//         </div>

//         {summaryVisible && (
//           <>
//             {/* Metric tiles */}
//             <div className="phase-summary-metrics">
//               {[
//                 { label: "Total", value: totals.total_plots, color: "#000000" },
//                 {
//                   label: "Available",
//                   value: totals.available_plot_count,
//                   color: "#4ade80",
//                 },
//                 {
//                   label: "Sold",
//                   value: totals.sold_plot_count,
//                   color: "#f87171",
//                 },
//               ].map(({ label, value, color }) => (
//                 <div key={label} className="phase-summary-metric">
//                   <p className="phase-summary-metric-label">{label}</p>
//                   <p className="phase-summary-metric-value" style={{ color }}>
//                     {value}
//                   </p>
//                 </div>
//               ))}
//             </div>

//             {/* Collapsible list toggle */}
//             <div onClick={() => setSummaryOpen((o) => !o)} className="phase-summary-list-toggle">
//               <span className="phase-summary-list-toggle-label">Phase-wise breakdown</span>
//               <span className="phase-summary-list-toggle-icon">{summaryOpen ? "▴" : "▾"}</span>
//             </div>

//             {/* Block rows */}
//             {summaryOpen && (
//               <ul className="phase-summary-block-list">
//                 {blocks.map((block, i) => {
//                   const isAvail = block.total_plots > 0 && block.available_plot_count > 0;
//                   const hasNoData = block.total_plots === 0;

//                   return (
//                     <li
//                       key={block.id}
//                       onClick={() => isAvail && navigate(`/${tenant}/floor/${block.id}`)}
//                       className={`phase-summary-block-item ${isAvail ? 'phase-summary-block-item-clickable' : ''}`}
//                     >
//                       {/* Status dot */}
//                       <span
//                         className={`phase-summary-status-dot ${
//                           hasNoData ? 'phase-summary-status-dot-no-data' :
//                           isAvail ? 'phase-summary-status-dot-available' : 'phase-summary-status-dot-sold'
//                         }`}
//                       />

//                       {/* Block name */}
//                       <span className={`phase-summary-block-name ${
//                         hasNoData ? 'phase-summary-block-name-no-data' :
//                         isAvail ? 'phase-summary-block-name-available' : 'phase-summary-block-name-sold'
//                       }`}>
//                         {block.block_name}
//                       </span>

//                       {!hasNoData && (
//                         <>
//                           <span className={`phase-summary-block-available ${
//                             isAvail ? 'phase-summary-block-available-active' : ''
//                           }`}>
//                             {block.available_plot_count}
//                           </span>
//                           <span className="phase-summary-block-total">
//                             /{block.total_plots}
//                           </span>
//                         </>
//                       )}

//                       {/* Sold-out badge */}
//                       {hasNoData && (
//                         <span className="phase-summary-badge phase-summary-badge-no-data">
//                           No data
//                         </span>
//                       )}

//                       {!hasNoData && !isAvail && (
//                         <span className="phase-summary-badge phase-summary-badge-sold">
//                           Sold out
//                         </span>
//                       )}
//                     </li>
//                   );
//                 })}
//               </ul>
//             )}

//             {/* Legend */}
//             <div className="phase-summary-legend">
//               {[
//                 ["#4ade80", "Available"],
//                 ["#6b7280", "Sold out"],
//               ].map(([color, label]) => (
//                 <div key={label} className="phase-summary-legend-item">
//                   <span className="phase-summary-legend-dot" style={{ background: color }} />
//                   <span className="phase-summary-legend-label">{label}</span>
//                 </div>
//               ))}
//             </div>
//           </>
//         )}
//       </div>
//     );
//   };

  

//   // ── Fetching state ──────────────────────────────────────────────────────
//   if (isFetching || !settingsLoaded) {
//     return (
//       <div className="viewer-fetching">
//         <div className="viewer-fetching-card">
//           {settings?.logo_dark_url ? (
//             <img
//               src={settings.logo_dark_url}
//               alt={settings?.company_name || "Logo"}
//               className="viewer-fetching-logo"
//             />
//           ) : (
//             <div className="viewer-fetching-mark">
//               {(settings?.company_name || "")[0] || ""}
//             </div>
//           )}
//           <div className="viewer-fetching-rule">
//             <div className="viewer-fetching-rule__fill" />
//           </div>
//           <div className="viewer-fetching-label">
//             {!settingsLoaded ? "Loading" : "Preparing your tour"}
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // ── Error state ─────────────────────────────────────────────────────────
//   if (fetchError || !panoramaUrl) {
//     return (
//       <div className="viewer-error">
//         <div className="viewer-error-card">
//           <h2 className="viewer-error-title">Tour unavailable</h2>
//           <p className="viewer-error-message">
//             {fetchError || "Panorama not uploaded yet for this master plan."}
//           </p>
//           <button onClick={() => navigate(`/${tenant}`)} className="viewer-error-btn">
//             Go back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // ── Main render ─────────────────────────────────────────────────────────
//   return (
//     <div className="apartment-viewer">
//       {/* Exit — the only persistent chrome, floats top-right */}
//       <button
//         onClick={() => navigate(`/${tenant}`)}
//         className={`viewer-exit-btn ${showHeader ? "is-visible" : ""}`}
//         aria-label="Exit tour"
//       >
//         ✕
//       </button>

//       {/* 360° panorama canvas — fills the whole viewport */}
//       <div ref={containerRef} className="viewer-panorama" />

//       {/* Phase Summary Panel — floats over panorama */}
//       {/* <PhaseSummaryPanel /> */}

//       {/* Loading overlay */}
//       <div
//         className={`viewer-loading ${isLoaded ? "viewer-loading-hidden" : ""}`}
//       >
//         <div className="viewer-loading-card">
//           {settings?.logo_dark_url ? (
//             <img
//               src={settings.logo_dark_url}
//               alt={settings?.company_name || "Logo"}
//               className="viewer-loading-logo"
//             />
//           ) : (
//             <div className="viewer-loading-mark">
//               {(settings?.company_name || "")[0] || ""}
//             </div>
//           )}
//           <div className="viewer-loading-rule">
//             <div
//               className="viewer-loading-rule__fill"
//               style={{ width: `${loadProgress}%` }}
//             />
//           </div>
//           <div className="viewer-loading-label">
//             {loadProgress < 100 ? "Preparing your tour" : "Ready"}
//           </div>
//         </div>
//       </div>

//       {/* Floor label */}
//       <div ref={labelRef} className="viewer-floor-label" />

//       {/* Transition overlay */}
//       <div ref={overlayRef} className="viewer-transition" />
//     </div>
//   );
// };

// export default ApartmentViewer;


import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Viewer } from "@photo-sphere-viewer/core";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import * as THREE from "three";
import { VRButton } from "three/examples/jsm/webxr/VRButton.js";
import { apiJson } from "../api/apiClient";
import { useSettings } from "../context/SettingsContext";
import "../styles/ApartmentViewer.css";

const toRad = (deg) => parseFloat((deg * (Math.PI / 180)).toFixed(6));

// ── Tenant-scoped auth guard ────────────────────────────────────────────
// ASSUMPTION: logged-in user info lives in localStorage as `auth_user` /
// `auth_token`, tagged with the tenant it belongs to under `auth_tenant`.
// If you're using an AuthContext/Provider instead, move this exact check
// into that provider's init effect, keyed off the tenant from the URL —
// the principle (clear identity the instant the URL tenant doesn't match
// the stored tenant) is what matters, not where it physically lives.
const useTenantScopedAuth = (tenant) => {
  useEffect(() => {
    if (!tenant) return;
    try {
      const storedTenant = localStorage.getItem("auth_tenant");
      if (storedTenant && storedTenant !== tenant) {
        localStorage.removeItem("auth_user");
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_tenant");
      }
    } catch (e) {
      console.warn("Tenant auth scope check failed:", e);
    }
  }, [tenant]);
};

const ApartmentViewer = () => {
  const { tenant, masterPlanId } = useParams();
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const overlayRef = useRef(null);
  const labelRef = useRef(null);
  const navigate = useNavigate();

  useTenantScopedAuth(tenant);

  const { settings, isLoaded: settingsLoaded } = useSettings();

  const [loadProgress, setLoadProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [panoramaUrl, setPanoramaUrl] = useState(null);
  const [hotspots, setHotspots] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [viewerInitialized, setViewerInitialized] = useState(false);

  // ── Summary panel state ─────────────────────────────────────────────────
  const [blockSummary, setBlockSummary] = useState(null);
  const [summaryVisible, setSummaryVisible] = useState(true);
  const [summaryOpen, setSummaryOpen] = useState(true);

  // ── WebXR / VR mode state ────────────────────────────────────────────────
  // Separate render path from the PSV viewer above — PSV (Photo Sphere
  // Viewer) doesn't support true immersive-vr headset sessions, so VR mode
  // mounts its own minimal Three.js scene sharing the same panoramaUrl,
  // and unmounts the PSV container while active.
  const vrContainerRef = useRef(null);
  const vrRendererRef = useRef(null);
  const [vrMode, setVrMode] = useState(false);
  const [vrSupported, setVrSupported] = useState(false);

  useEffect(() => {
    if (navigator.xr) {
      navigator.xr
        .isSessionSupported("immersive-vr")
        .then(setVrSupported)
        .catch(() => setVrSupported(false));
    }
  }, []);

  // ── Helper: build image URL with tenant ──────────────────────────────────
  const getFullImageUrl = useCallback((path) => {
    if (!path) return null;

    // If it's already a full URL, return it as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    // Clean the path - remove leading slash if present
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    // Use settings-based img_url or fallback to environment variable
    const baseUrl = settings?.img_url || process.env.REACT_APP_IMG_URL || '';

    // If baseUrl ends with slash, don't add another one
    const url = baseUrl.endsWith('/')
      ? `${baseUrl}${cleanPath}`
      : `${baseUrl}/${cleanPath}`;

    return url;
  }, [settings]);

  // ── Find a hotspot's block stats from the background-loaded summary ────
  const getBlockStats = useCallback(
    (blockId) => {
      if (!blockSummary?.blocks) return null;
      const block = blockSummary.blocks.find((b) => b.id === blockId);
      if (!block) return null;
      return {
        available: block.available_plot_count,
        total: block.total_plots,
      };
    },
    [blockSummary],
  );

  // ── Build a marker's inner HTML — dot + hover availability popup ───────
  const buildMarkerHtml = useCallback((h, stats) => {
    const body = stats
      ? `<div class="viewer-marker-popup__stats">
           <span class="viewer-marker-popup__available">${stats.available}</span>
           <span class="viewer-marker-popup__sep">/</span>
           <span class="viewer-marker-popup__total">${stats.total}</span>
         </div>
         <div class="viewer-marker-popup__word">
           ${stats.available > 0 ? "plots available" : "sold out"}
         </div>`
      : `<div class="viewer-marker-popup__word">Checking availability…</div>`;

    return `
      <div class="viewer-marker-dot">
        <span class="viewer-marker-dot__core"></span>
        <span class="viewer-marker-dot__ring"></span>
        <div class="viewer-marker-popup ${stats && stats.available === 0 ? "is-sold-out" : ""}">
          <div class="viewer-marker-popup__label">${h.label}</div>
          ${body}
        </div>
      </div>`;
  }, []);

  // ── FETCH 1: hotspots + panorama (critical — viewer depends on this) ────
  useEffect(() => {
    if (!tenant || !masterPlanId) {
      setFetchError("No tenant or master plan ID provided");
      setIsFetching(false);
      return;
    }

    const fetchCriticalData = async () => {
      try {
        // Hotspots - using apiJson for tenant-aware requests
        const hotspotData = await apiJson(tenant, `/hotspots?master_plan_id=${masterPlanId}`);
        setHotspots(hotspotData);

        // Master plan — panorama URL
        const planData = await apiJson(tenant, `/master-plan/${masterPlanId}`);

        if (!planData.panorama_url) {
          throw new Error("No panorama uploaded for this master plan");
        }

        // Get the full image URL with tenant context
        const fullPanoramaUrl = getFullImageUrl(planData.panorama_url);

        if (!fullPanoramaUrl) {
          throw new Error("Failed to construct panorama URL");
        }

        setPanoramaUrl(fullPanoramaUrl);

        // Kick off the non-critical summary fetch in background
        if (planData.project_id) {
          fetchSummaryInBackground(planData.project_id);
        }
      } catch (err) {
        console.error("Critical fetch error:", err);
        setFetchError(err.message);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCriticalData();
  }, [tenant, masterPlanId, getFullImageUrl]);

  // ── FETCH 2: block summary (non-critical — runs in background) ──────────
  const fetchSummaryInBackground = async (projectId) => {
    try {
      const data = await apiJson(tenant, `/blocks/summary/${projectId}/${masterPlanId}`);
      setBlockSummary(data);
    } catch (err) {
      // Completely non-fatal — viewer is already running
      console.warn("Block summary unavailable:", err.message);
    }
  };

  // ── Initialize PSV viewer (skipped entirely while vrMode is active) ─────
  useEffect(() => {
    // Don't proceed if any required data is missing, viewer already exists,
    // or VR mode has taken over the screen.
    if (!panoramaUrl || !hotspots || !containerRef.current || viewerRef.current || vrMode) {
      return;
    }

    let intervalId;
    let animationId;
    let cleanupFunctions = [];
    let viewerInstance = null;

    const initViewer = async () => {
      try {
        // Reset progress
        let progress = 0;
        intervalId = setInterval(() => {
          progress += Math.random() * 15;
          if (progress >= 90) {
            progress = 90;
            clearInterval(intervalId);
          }
          setLoadProgress(Math.min(Math.round(progress), 90));
        }, 150);

        const psvMarkers = hotspots.map((h) => ({
          id: `floor-${h.id}`,
          position: {
            yaw: toRad(parseFloat(h.yaw)),
            pitch: toRad(parseFloat(h.pitch)),
          },
          html: buildMarkerHtml(h, getBlockStats(h.block_id)),
          size: { width: 22, height: 22 },
          anchor: "center center",
          data: {
            hotspotId: h.id,
            blockId: h.block_id,
            label: h.label,
            yawRad: toRad(parseFloat(h.yaw)),
            pitchRad: toRad(parseFloat(h.pitch)),
          },
        }));

        // Create viewer instance
        viewerInstance = new Viewer({
          container: containerRef.current,
          panorama: panoramaUrl,
          defaultZoomLvl: 50,
          mousewheelCtrlKey: false,
          loadingImg: null,
          loadingTxt: "",
          navbar: ["zoom", "move", "caption", "fullscreen"],
          plugins: [[MarkersPlugin, { markers: psvMarkers }]],
        });

        viewerRef.current = viewerInstance;

        // Handle ready event
        viewerInstance.addEventListener("ready", () => {
          clearInterval(intervalId);
          setLoadProgress(100);

          setTimeout(() => {
            setIsLoaded(true);
            setShowHeader(true);
            setViewerInitialized(true);
          }, 500);

          // Auto-rotate effect
          let angle = 0;
          let isInteracting = false;
          let resumeTimeout = null;

          const rotate = () => {
            if (!isInteracting && viewerRef.current) {
              angle += 0.003;
              try {
                viewerRef.current.rotate({ yaw: Math.sin(angle) * 0.08, pitch: 0 });
              } catch (e) {
                // Ignore rotation errors
              }
            }
            if (viewerRef.current) {
              animationId = requestAnimationFrame(rotate);
            }
          };
          animationId = requestAnimationFrame(rotate);

          const onInteractionStart = () => {
            isInteracting = true;
            clearTimeout(resumeTimeout);
          };

          const onInteractionEnd = () => {
            clearTimeout(resumeTimeout);
            resumeTimeout = setTimeout(() => {
              isInteracting = false;
            }, 2000);
          };

          const container = containerRef.current;
          if (container) {
            container.addEventListener("mousedown", onInteractionStart);
            container.addEventListener("mouseup", onInteractionEnd);
            container.addEventListener("touchstart", onInteractionStart);
            container.addEventListener("touchend", onInteractionEnd);
          }

          cleanupFunctions.push(() => {
            if (animationId) cancelAnimationFrame(animationId);
            clearTimeout(resumeTimeout);
            if (container) {
              container.removeEventListener("mousedown", onInteractionStart);
              container.removeEventListener("mouseup", onInteractionEnd);
              container.removeEventListener("touchstart", onInteractionStart);
              container.removeEventListener("touchend", onInteractionEnd);
            }
          });
        });

        // Handle error event
        viewerInstance.addEventListener("error", (e) => {
          console.error('Viewer error:', e);
          setFetchError(`Failed to load panorama: ${e.message || 'Unknown error'}`);
        });

        // Handle markers plugin
        const markersPlugin = viewerInstance.getPlugin(MarkersPlugin);
        if (markersPlugin) {
          markersPlugin.addEventListener("select-marker", ({ marker }) => {
            const { blockId, label, yawRad, pitchRad } = marker.data;

            hotspots.forEach((h) => {
              try {
                markersPlugin.updateMarker({ id: `floor-${h.id}`, opacity: 0 });
              } catch (e) {}
            });

            if (labelRef.current) {
              labelRef.current.textContent = label;
              labelRef.current.classList.add("viewer-floor-label-visible");
            }

            viewerInstance
              ?.animate({ yaw: yawRad, pitch: pitchRad, zoom: 55, speed: "4rpm" })
              .then(() =>
                viewerInstance?.animate({
                  yaw: yawRad,
                  pitch: pitchRad,
                  zoom: 100,
                  speed: "20rpm",
                }),
              )
              .then(() => {
                setIsTransitioning(true);
                if (overlayRef.current)
                  overlayRef.current.classList.add("viewer-transition-active");
                setTimeout(() => navigate(`/${tenant}/floor/${blockId}`), 450);
              });
          });
        }

        cleanupFunctions.push(() => {
          if (viewerRef.current) {
            try {
              viewerRef.current.destroy();
            } catch (e) {
              console.warn('Error destroying viewer:', e);
            }
            viewerRef.current = null;
            setViewerInitialized(false);
          }
        });

      } catch (err) {
        console.error('Error initializing viewer:', err);
        setFetchError(`Failed to initialize viewer: ${err.message}`);
        clearInterval(intervalId);
      }
    };

    initViewer();

    return () => {
      clearInterval(intervalId);
      if (animationId) cancelAnimationFrame(animationId);
      cleanupFunctions.forEach((fn) => {
        try {
          fn();
        } catch (e) {
          console.warn('Error during cleanup:', e);
        }
      });
    };
  }, [panoramaUrl, hotspots, navigate, tenant, buildMarkerHtml, getBlockStats, vrMode]);

  // ── Refresh marker popups once block summary arrives in background ─────
  useEffect(() => {
    if (!viewerRef.current || !hotspots) return;
    const markersPlugin = viewerRef.current.getPlugin(MarkersPlugin);
    if (!markersPlugin) return;

    hotspots.forEach((h) => {
      try {
        markersPlugin.updateMarker({
          id: `floor-${h.id}`,
          html: buildMarkerHtml(h, getBlockStats(h.block_id)),
        });
      } catch (e) {
        // Marker may not exist yet if hotspots changed mid-flight — ignore.
      }
    });
  }, [blockSummary, hotspots, buildMarkerHtml, getBlockStats]);

  useEffect(() => () => setIsTransitioning(false), []);

  // ── VR mode: minimal Three.js WebXR scene sharing the same panorama ────
  // Mounts only while vrMode is true; the PSV effect above bails out
  // whenever vrMode is true, so the two renderers never run simultaneously.
  useEffect(() => {
    if (!vrMode || !panoramaUrl || !vrContainerRef.current) return;

    const container = vrContainerRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.xr.enabled = true;
    container.appendChild(renderer.domElement);
    vrRendererRef.current = renderer;

    // "Enter VR" button, provided by Three.js — appended to <body> so it
    // isn't clipped by this container's stacking context.
    const vrButton = VRButton.createButton(renderer);
    vrButton.style.position = "fixed";
    vrButton.style.bottom = "24px";
    vrButton.style.zIndex = "20";
    document.body.appendChild(vrButton);

    // Equirectangular panorama sphere — same panoramaUrl the PSV viewer
    // uses. Scale.x = -1 flips normals inward so the camera at the
    // sphere's center sees the texture from the inside.
    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);

    const texture = new THREE.TextureLoader().load(panoramaUrl);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshBasicMaterial({ map: texture });
    const sphere = new THREE.Mesh(geometry, material);
    scene.add(sphere);

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", onResize);

    // WebXR requires renderer.setAnimationLoop, NOT requestAnimationFrame —
    // this lets Three.js drive the render loop from the headset's own
    // refresh cycle once an immersive-vr session starts.
    renderer.setAnimationLoop(() => {
      renderer.render(scene, camera);
    });

    return () => {
      renderer.setAnimationLoop(null);
      window.removeEventListener("resize", onResize);
      vrButton.remove();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      texture.dispose();
      renderer.dispose();
      vrRendererRef.current = null;
    };
  }, [vrMode, panoramaUrl]);

  const toggleFullscreen = useCallback(() => {
    if (viewerRef.current) {
      try {
        viewerRef.current.toggleFullscreen();
      } catch (e) {
        console.warn('Fullscreen error:', e);
      }
    }
  }, []);

  const resetView = useCallback(() => {
    if (viewerRef.current) {
      try {
        viewerRef.current.animate({ yaw: 0, pitch: 0, zoom: 50, speed: "8rpm" });
      } catch (e) {
        console.warn('Reset view error:', e);
      }
    }
  }, []);

  // ── Phase Summary Panel ─────────────────────────────────────────────────
  const PhaseSummaryPanel = () => {
    if (!blockSummary || !isLoaded) return null;
    const { blocks, totals } = blockSummary;

    // ── No data guard ──────────────────────────────────────────────────────
    if (!totals || totals.total_plots === 0) {
      return (
        <div className="phase-summary-panel">
          <div className="phase-summary-header">
            <span className="phase-summary-title">Phase Summary</span>
          </div>
          <div className="phase-summary-no-data">
            <span className="phase-summary-no-data-icon">🏗️</span>
            <p className="phase-summary-no-data-text">No plot data available</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`phase-summary-panel ${showHeader ? 'phase-summary-panel-with-header' : ''}`}>
        {/* Toggle header */}
        <div onClick={() => setSummaryVisible((v) => !v)} className="phase-summary-toggle-header">
          <span className="phase-summary-title">Phase Summary</span>
          <span className="phase-summary-toggle-icon">{summaryVisible ? "▲" : "▼"}</span>
        </div>

        {summaryVisible && (
          <>
            {/* Metric tiles */}
            <div className="phase-summary-metrics">
              {[
                { label: "Total", value: totals.total_plots, color: "#000000" },
                {
                  label: "Available",
                  value: totals.available_plot_count,
                  color: "#4ade80",
                },
                {
                  label: "Sold",
                  value: totals.sold_plot_count,
                  color: "#f87171",
                },
              ].map(({ label, value, color }) => (
                <div key={label} className="phase-summary-metric">
                  <p className="phase-summary-metric-label">{label}</p>
                  <p className="phase-summary-metric-value" style={{ color }}>
                    {value}
                  </p>
                </div>
              ))}
            </div>

            {/* Collapsible list toggle */}
            <div onClick={() => setSummaryOpen((o) => !o)} className="phase-summary-list-toggle">
              <span className="phase-summary-list-toggle-label">Phase-wise breakdown</span>
              <span className="phase-summary-list-toggle-icon">{summaryOpen ? "▴" : "▾"}</span>
            </div>

            {/* Block rows */}
            {summaryOpen && (
              <ul className="phase-summary-block-list">
                {blocks.map((block, i) => {
                  const isAvail = block.total_plots > 0 && block.available_plot_count > 0;
                  const hasNoData = block.total_plots === 0;

                  return (
                    <li
                      key={block.id}
                      onClick={() => isAvail && navigate(`/${tenant}/floor/${block.id}`)}
                      className={`phase-summary-block-item ${isAvail ? 'phase-summary-block-item-clickable' : ''}`}
                    >
                      {/* Status dot */}
                      <span
                        className={`phase-summary-status-dot ${
                          hasNoData ? 'phase-summary-status-dot-no-data' :
                          isAvail ? 'phase-summary-status-dot-available' : 'phase-summary-status-dot-sold'
                        }`}
                      />

                      {/* Block name */}
                      <span className={`phase-summary-block-name ${
                        hasNoData ? 'phase-summary-block-name-no-data' :
                        isAvail ? 'phase-summary-block-name-available' : 'phase-summary-block-name-sold'
                      }`}>
                        {block.block_name}
                      </span>

                      {!hasNoData && (
                        <>
                          <span className={`phase-summary-block-available ${
                            isAvail ? 'phase-summary-block-available-active' : ''
                          }`}>
                            {block.available_plot_count}
                          </span>
                          <span className="phase-summary-block-total">
                            /{block.total_plots}
                          </span>
                        </>
                      )}

                      {/* Sold-out badge */}
                      {hasNoData && (
                        <span className="phase-summary-badge phase-summary-badge-no-data">
                          No data
                        </span>
                      )}

                      {!hasNoData && !isAvail && (
                        <span className="phase-summary-badge phase-summary-badge-sold">
                          Sold out
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Legend */}
            <div className="phase-summary-legend">
              {[
                ["#4ade80", "Available"],
                ["#6b7280", "Sold out"],
              ].map(([color, label]) => (
                <div key={label} className="phase-summary-legend-item">
                  <span className="phase-summary-legend-dot" style={{ background: color }} />
                  <span className="phase-summary-legend-label">{label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  

  // ── Fetching state ──────────────────────────────────────────────────────
  if (isFetching || !settingsLoaded) {
    return (
      <div className="viewer-fetching">
        <div className="viewer-fetching-card">
          {settings?.logo_dark_url ? (
            <img
              src={settings.logo_dark_url}
              alt={settings?.company_name || "Logo"}
              className="viewer-fetching-logo"
            />
          ) : (
            <div className="viewer-fetching-mark">
              {(settings?.company_name || "")[0] || ""}
            </div>
          )}
          <div className="viewer-fetching-rule">
            <div className="viewer-fetching-rule__fill" />
          </div>
          <div className="viewer-fetching-label">
            {!settingsLoaded ? "Loading" : "Preparing your tour"}
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (fetchError || !panoramaUrl) {
    return (
      <div className="viewer-error">
        <div className="viewer-error-card">
          <h2 className="viewer-error-title">Tour unavailable</h2>
          <p className="viewer-error-message">
            {fetchError || "Panorama not uploaded yet for this master plan."}
          </p>
          <button onClick={() => navigate(`/${tenant}`)} className="viewer-error-btn">
            Go back
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────
  return (
    <div className="apartment-viewer">
      {/* Exit — the only persistent chrome, floats top-right */}
      <button
        onClick={() => (vrMode ? setVrMode(false) : navigate(`/${tenant}`))}
        className={`viewer-exit-btn ${showHeader || vrMode ? "is-visible" : ""}`}
        aria-label={vrMode ? "Exit VR" : "Exit tour"}
      >
        ✕
      </button>

      {/* Enter VR — only shown when a headset session is actually supported
          and we're not already in VR mode */}
      {vrSupported && !vrMode && (
        <button
          onClick={() => setVrMode(true)}
          className={`viewer-exit-btn ${showHeader ? "is-visible" : ""}`}
          style={{ right: 70 }}
          aria-label="Enter VR"
        >
          🥽
        </button>
      )}

      {/* 360° panorama canvas — PSV normally, Three.js WebXR scene in VR mode */}
      {vrMode ? (
        <div ref={vrContainerRef} className="viewer-panorama" />
      ) : (
        <div ref={containerRef} className="viewer-panorama" />
      )}

      {/* Phase Summary Panel — floats over panorama */}
      {/* <PhaseSummaryPanel /> */}

      {/* Loading overlay — PSV loading progress only, hidden in VR mode */}
      {!vrMode && (
        <div
          className={`viewer-loading ${isLoaded ? "viewer-loading-hidden" : ""}`}
        >
          <div className="viewer-loading-card">
            {settings?.logo_dark_url ? (
              <img
                src={settings.logo_dark_url}
                alt={settings?.company_name || "Logo"}
                className="viewer-loading-logo"
              />
            ) : (
              <div className="viewer-loading-mark">
                {(settings?.company_name || "")[0] || ""}
              </div>
            )}
            <div className="viewer-loading-rule">
              <div
                className="viewer-loading-rule__fill"
                style={{ width: `${loadProgress}%` }}
              />
            </div>
            <div className="viewer-loading-label">
              {loadProgress < 100 ? "Preparing your tour" : "Ready"}
            </div>
          </div>
        </div>
      )}

      {/* Floor label */}
      <div ref={labelRef} className="viewer-floor-label" />

      {/* Transition overlay */}
      <div ref={overlayRef} className="viewer-transition" />
    </div>
  );
};

export default ApartmentViewer;
